import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Badge, Label, Small, Tooltip } from "@fronterahealth/frontera-ui-components";

import { TargetRow } from "@components/TreatmentPlan/ProgrammingGoalCard/TargetRow";
import { getStatusAppearance, getStatusIcon } from "@components/TreatmentPlan/ProgrammingGoalCard/helper";
import { Goal, GoalList, Target, useClientProgrammingData } from "@providers/ClientProgrammingProvider";

interface FilteredGoalsAndTargetsProps {
  filteredGoals: GoalList;
  filteredTargets: Target[];
}

interface FilteredGoalCardProps {
  goal: Goal;
  highlightText: (text: string | null | undefined) => string | (string | JSX.Element)[] | null | undefined;
}

const FilteredGoalCard: React.FC<FilteredGoalCardProps> = ({ goal, highlightText }) => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const { selectedGoal, selectedTarget, setSelectedGoalId, searchGoalText } = useClientProgrammingData();

  const isSelected = selectedTarget ? false : selectedGoal?.id === goal.id;
  const handleGoalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedGoalId(goal.id);
    navigate(`/clients/${clientId}/programming/goal/${goal.id}?search=${searchGoalText}`);
  };

  const Icon = goal.status ? getStatusIcon(goal.status) : React.Fragment;
  return (
    <div
      className={`rounded-xl space-y-2 border p-4 mb-3 bg-white shadow-xs transition cursor-pointer ${
        isSelected ? "border-green-700 border-2 " : "border-limestone-200 "
      }`}
      onClick={handleGoalClick}
    >
      <div
        className={clsx(
          "flex justify-between pr-2 items-center",
          !goal.targets?.length && "pointer-events-none cursor-not-allowed",
        )}
      >
        <div className="flex w-full gap-2 items-center justify-start">
          <Small className="w-[90%] truncate ph-no-capture" colorType="primary">
            <label title={goal.name || "Unknown goal name"}>{highlightText(goal.name)}</label>
          </Small>
        </div>

        <div className="flex flex-row gap-2 items-center">
          {goal.status ? (
            <Badge
              text={
                <div className="flex flex-row gap-1">
                  <Icon className="h-3 w-3" />
                </div>
              }
              className="p-1!"
              appearance={goal.status ? getStatusAppearance(goal.status) : "neutral"}
            />
          ) : null}
          <Tooltip text={`${goal.targets?.length || 0} targets`} showInfoIcon={false}>
            <Label size="extra-small">{goal.targets?.length || 0}</Label>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export const FilteredGoalsAndTargets: React.FC<FilteredGoalsAndTargetsProps> = ({ filteredGoals, filteredTargets }) => {
  const [collapsedGoals, setCollapsedGoals] = useState(false);
  const [collapsedTargets, setCollapsedTargets] = useState(false);
  const { searchGoalText } = useClientProgrammingData();

  const escapeRegExp = (string: string) => string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const highlightText = useMemo(() => {
    return (text: string | null | undefined) => {
      if (!text || !searchGoalText) return text;

      const escapedSearchText = escapeRegExp(searchGoalText);
      const regex = new RegExp(`(${escapedSearchText})`, "gi");

      const parts = text.split(regex);

      return parts.map((part, index) =>
        part.toLowerCase() === searchGoalText.toLowerCase() ? (
          <span key={index} className="bg-orange-200">
            {part}
          </span>
        ) : (
          part
        ),
      );
    };
  }, [searchGoalText]);

  const hasResults = (filteredGoals?.length || 0) > 0 || (filteredTargets?.length || 0) > 0;

  return (
    <div className="flex flex-col">
      {!hasResults ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <Label size="small" colorType="secondary">
            No results found for "{searchGoalText}"
          </Label>
          <Label size="extra-small" colorType="secondary" className="mt-1">
            Try adjusting your search terms
          </Label>
        </div>
      ) : (
        <>
          {filteredGoals?.length ? (
            <div>
              <div
                className="flex flex-row gap-1 items-center w-full "
                onClick={(e) => {
                  e.stopPropagation();
                  setCollapsedGoals(!collapsedGoals);
                }}
              >
                {collapsedGoals ? (
                  <ChevronRightIcon className="text-text-secondary h-3 w-3" />
                ) : (
                  <ChevronDownIcon className="text-text-secondary h-3 w-3" />
                )}
                <Label size="extra-small" displayType="loud">
                  Goals ({filteredGoals.length})
                </Label>
              </div>
              {!collapsedGoals && (
                <div className="mt-2">
                  {filteredGoals.map((goal) => (
                    <FilteredGoalCard key={goal.id} goal={goal} highlightText={highlightText} />
                  ))}
                </div>
              )}
            </div>
          ) : null}
          {filteredTargets?.length ? (
            <div>
              <div
                className="flex flex-row gap-1 items-center w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  setCollapsedTargets(!collapsedTargets);
                }}
              >
                {collapsedTargets ? (
                  <ChevronRightIcon className="text-text-secondary h-3 w-3" />
                ) : (
                  <ChevronDownIcon className="text-text-secondary h-3 w-3" />
                )}
                <Label size="extra-small" displayType="loud">
                  Targets ({filteredTargets.length})
                </Label>
              </div>
              {!collapsedTargets && (
                <div className="mt-2 space-y-2">
                  {filteredTargets.map((target) => (
                    <TargetRow key={target.id} target={target} highlightText={highlightText} isFilteredtarget={true} />
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};
